import * as Blockly from 'blockly/core';
import { useBlockModalStore } from '@/stores/useBlockModalStore';
import { useBlockModalWifiStore } from '@/stores/useBlockModalWifiStore';
import type { WifiProfile } from '@/stores/useBlockModalWifiStore';
import { darken } from '@/(routers)/boards/editor/block/blockTheme';

const FIELD_HEIGHT = 42;
const PADDING = 8;
const FIELD_MIN_WIDTH = 0;
const TEXT_X = PADDING;

export class FieldWifi extends Blockly.Field<string> {
    private profile_: WifiProfile | null = null;
    private labelEl_: SVGTextElement | null = null;
    private onMouseEnter_: (() => void) | null = null;
    private onMouseLeave_: (() => void) | null = null;

    static override fromJson(options: Blockly.FieldConfig & { profile?: WifiProfile }): FieldWifi {
        return new FieldWifi(options.profile ?? null);
    }

    constructor(profile: WifiProfile | null = null) {
        super(profile ? JSON.stringify(profile) : '');
        this.profile_ = profile;
        this.SERIALIZABLE = true;
    }

    override initView(): void {
        this.borderRect_ = Blockly.utils.dom.createSvgElement(
            Blockly.utils.Svg.RECT,
            { rx: 6, ry: 6, x: 0, y: 0, width: FIELD_MIN_WIDTH, height: FIELD_HEIGHT },
            this.fieldGroup_,
        );

        this.labelEl_ = Blockly.utils.dom.createSvgElement(
            Blockly.utils.Svg.TEXT,
            {
                x: TEXT_X,
                y: FIELD_HEIGHT / 2 + 6,
                'font-size': '17px',
                'font-weight': '500',
                'font-family': 'Corporate Logo, sans-serif',
                style: 'fill: white !important;',
            },
            this.fieldGroup_,
        ) as SVGTextElement;

        this.updateDisplay_();

        requestAnimationFrame(() => {
            this.applyStyle_();
            this.updateDisplay_();
            (this.getSourceBlock() as Blockly.BlockSvg)?.render();

            this.onMouseEnter_ = () => this.applyStyle_(true);
            this.onMouseLeave_ = () => this.applyStyle_(false);
            this.fieldGroup_?.addEventListener('mouseenter', this.onMouseEnter_);
            this.fieldGroup_?.addEventListener('mouseleave', this.onMouseLeave_);
        });
    }

    override dispose(): void {
        if (this.fieldGroup_) {
            if (this.onMouseEnter_) {
                this.fieldGroup_.removeEventListener('mouseenter', this.onMouseEnter_);
                this.onMouseEnter_ = null;
            }
            if (this.onMouseLeave_) {
                this.fieldGroup_.removeEventListener('mouseleave', this.onMouseLeave_);
                this.onMouseLeave_ = null;
            }
        }
        super.dispose();
    }

    private applyStyle_(hover = false): void {
        if (!this.borderRect_) return;
        const colour = this.getSourceBlock()?.getColour() ?? '#1565C0';
        const fillColour = this.profile_ === null ? darken(colour) : colour;
        this.borderRect_.setAttribute(
            'style',
            `fill: ${fillColour} !important; stroke: ${
                hover ? 'rgba(255,255,255,0.9)' : 'rgba(0,0,0,0.3)'
            }; stroke-width: ${hover ? 2 : 1};`,
        );
    }

    private calcWidth_(): number {
        if (!this.profile_) return FIELD_MIN_WIDTH;
        const text = this.profile_.networkName;
        let w = 0;
        for (const char of text) {
            w += char.charCodeAt(0) > 127 ? 14 : 8;
        }
        return PADDING + w + PADDING;
    }

    private updateDisplay_(): void {
        if (!this.labelEl_) return;
        this.labelEl_.textContent = this.profile_ ? this.profile_.networkName : '未接続';
        const width = this.calcWidth_();
        this.borderRect_?.setAttribute('width', String(width));
        this.size_ = new Blockly.utils.Size(width, FIELD_HEIGHT);
        this.applyStyle_();

        requestAnimationFrame(() => {
            if (!this.labelEl_ || !this.borderRect_) return;
            try {
                const bbox = this.labelEl_.getBBox();
                if (bbox.width > 0) {
                    const realWidth = Math.max(FIELD_MIN_WIDTH, PADDING + bbox.width + PADDING);
                    this.borderRect_.setAttribute('width', String(realWidth));
                    this.size_ = new Blockly.utils.Size(realWidth, FIELD_HEIGHT);
                    (this.getSourceBlock() as Blockly.BlockSvg)?.render();
                }
            } catch {}
        });

        (this.getSourceBlock() as Blockly.BlockSvg)?.render();
    }

    public setProfile(profile: WifiProfile | null): void {
        const oldJson = JSON.stringify(this.profile_);
        this.profile_ = profile;
        const json = profile ? JSON.stringify(profile) : '';
        this.setValue(json);
        const block = this.getSourceBlock();
        if (block) {
            Blockly.Events.fire(new Blockly.Events.BlockChange(block, 'field', this.name ?? '', oldJson, json));
        }
    }

    public getProfile(): WifiProfile | null {
        return this.profile_;
    }

    override doValueUpdate_(newValue: string): void {
        try {
            this.profile_ = newValue ? (JSON.parse(newValue) as WifiProfile) : null;
        } catch {
            this.profile_ = null;
        }
        if (this.fieldGroup_) {
            this.updateDisplay_();
        }
    }

    override getValue(): string {
        return this.profile_ ? JSON.stringify(this.profile_) : '';
    }

    override getSize(): Blockly.utils.Size {
        if (this.borderRect_) {
            const w = parseFloat(this.borderRect_.getAttribute('width') ?? '0');
            if (w > 0) {
                this.size_ = new Blockly.utils.Size(w, FIELD_HEIGHT);
                return this.size_;
            }
        }
        const width = this.calcWidth_();
        this.size_ = new Blockly.utils.Size(width, FIELD_HEIGHT);
        return this.size_;
    }

    override showEditor_(): void {
        const { setFieldRef } = useBlockModalWifiStore.getState();
        const { openModal } = useBlockModalStore.getState();
        setFieldRef({
            setProfile: (profile) => this.setProfile(profile),
            getProfile: () => this.getProfile(),
        });
        openModal('wifi');
    }
}
